import { useRef, useMemo, useState, useEffect } from 'react';
import { useFrame, useLoader } from '@react-three/fiber';
import { Environment } from '@react-three/drei';
import * as THREE from 'three';

const GLOBE_RADIUS = 5;

const CITIES = [
  [139.6917, 35.6895], // Tokyo
  [-74.0059, 40.7128], // New York
  [2.3522, 48.8566], // Paris
  [151.2093, -33.8688], // Sydney
  [-118.2437, 34.0522], // Los Angeles
  [55.2708, 25.2048], // Dubai
  [-43.1729, -22.9068], // Rio de Janeiro
  [28.0339, -26.2023], // Johannesburg
  [116.4074, 39.9042], // Beijing
  [77.2090, 28.6139], // New Delhi
  [37.6173, 55.7558], // Moscow
  [-0.1276, 51.5074], // London
  [-99.1332, 19.4326], // Mexico City
  [13.4050, 52.5200], // Berlin
  [135.5023, 34.6937], // Osaka
  [106.8456, -6.2088], // Jakarta
  [-58.3816, -34.6037], // Buenos Aires
  [30.0444, 31.2357], // Cairo
  [121.4737, 31.2304], // Shanghai
  [100.5018, 13.7563], // Bangkok
  [-122.4194, 37.7749], // San Francisco
  [18.4241, -33.9249], // Cape Town
  [88.3639, 22.5726], // Kolkata
  [-87.6298, 41.8781], // Chicago
  [114.1095, 22.3964], // Hong Kong
  [4.9041, 52.3676], // Amsterdam
  [-51.2177, -30.0346], // Porto Alegre
  [31.7683, 39.9334], // Ankara
];

function Ring({ rotation = [0, 0, 0], scale = 1, speed = 1 }) {
  const ref = useRef<THREE.Mesh>(null);

  useFrame(({ clock }) => {
    if (!ref.current) return;
    ref.current.rotation.x = Math.sin(clock.getElapsedTime() * speed * 0.5) * 0.3 + rotation[0];
    ref.current.rotation.y = clock.getElapsedTime() * speed * 0.5 + rotation[1];
  });

  return (
    <mesh ref={ref} rotation={[0, 0, 0]} scale={scale}>
      <ringGeometry args={[GLOBE_RADIUS + 0.2, GLOBE_RADIUS + 0.5, 128]} />
      <meshBasicMaterial color="#4cc9f0" transparent opacity={0.4} side={THREE.DoubleSide} />
    </mesh>
  );
}

function latLongToVector3(lat: number, long: number, radius: number): THREE.Vector3 {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (long + 180) * (Math.PI / 180);

  const x = -(radius * Math.sin(phi) * Math.cos(theta));
  const z = radius * Math.sin(phi) * Math.sin(theta);
  const y = radius * Math.cos(phi);

  return new THREE.Vector3(x, y, z);
}

function CityDot({ position }: { position: [number, number, number] }) {
  const ref = useRef<THREE.Mesh>(null);

  useFrame(({ clock }) => {
    if (!ref.current) return;
    ref.current.scale.setScalar(Math.sin(clock.getElapsedTime() * 2) * 0.2 + 1);
  });

  return (
    <mesh ref={ref} position={position}>
      <sphereGeometry args={[0.05, 16, 16]} />
      <meshBasicMaterial color="#4cc9f0" />
    </mesh>
  );
}

export function Scene() {
  const globeRef = useRef<THREE.Mesh>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previousMousePosition, setPreviousMousePosition] = useState({ x: 0, y: 0 });
  const [rotationVelocity, setRotationVelocity] = useState({ x: 0, y: 0 });
  const [shouldAutoRotate, setShouldAutoRotate] = useState(true);
  const autoRotationSpeed = 0.004;
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const velocityThreshold = 0.0001;
  const [backgroundColor, setBackgroundColor] = useState('#1f2937'); // Default to dark mode

  const cityPositions = useMemo(() => {
    return CITIES.map(([long, lat]) =>
      latLongToVector3(lat, long, GLOBE_RADIUS).toArray() as [number, number, number]
    );
  }, []);

  // Pick a texture at mount time and let useLoader fetch it robustly
  const texturePath = useMemo(() => {
    const candidates = ['/earth-day.jpg', '/earth-water.png', '/earth-blue-marble.jpg'];
    return candidates[Math.floor(Math.random() * candidates.length)];
  }, []);

  const globeMap = useLoader(THREE.TextureLoader, texturePath);
  globeMap.colorSpace = THREE.SRGBColorSpace;

  // Check theme from localStorage and update background color
  useEffect(() => {
    const updateTheme = () => {
      try {
        const savedTheme = localStorage.getItem('theme');
        setBackgroundColor(savedTheme === 'dark' ? '#1f2937' : '#ffffff');
      } catch (error) {
        console.error('Error reading theme from localStorage:', error);
        setBackgroundColor('#ffffff'); // Fallback to light mode
      }
    };

    // Initial theme check
    updateTheme();

    // Listen for storage changes (e.g., theme toggle in App.tsx)
    window.addEventListener('storage', updateTheme);

    // Listen for direct localStorage changes in the same tab
    const interval = setInterval(() => {
      updateTheme();
    }, 1000); // Poll every 1 second as a fallback

    return () => {
      window.removeEventListener('storage', updateTheme);
      clearInterval(interval);
    };
  }, []);

  const isRotationStopped = () => {
    return (
      Math.abs(rotationVelocity.x) < velocityThreshold &&
      Math.abs(rotationVelocity.y) < velocityThreshold
    );
  };

  const resetInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    setShouldAutoRotate(true);

    inactivityTimerRef.current = setTimeout(() => {
      if (!isDragging && isRotationStopped()) {
        setShouldAutoRotate(true);
      }
    }, 2000);
  };

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
	  // ✅ ADD THIS LINE: If the click isn't on the canvas, do nothing.
	  if ((event.target as HTMLElement).tagName !== 'CANVAS') return;
      setIsDragging(true);
      setPreviousMousePosition({ x: event.clientX, y: event.clientY });
      setShouldAutoRotate(true);
    };

    const handleMouseMove = (event: MouseEvent) => {
      resetInactivityTimer();

      if (!isDragging || !globeRef.current) return;

      const deltaMove = {
        x: event.clientX - previousMousePosition.x,
        y: event.clientY - previousMousePosition.y,
      };

      const rotationSpeed = 0.005;
      globeRef.current.rotation.y += deltaMove.x * rotationSpeed;
      globeRef.current.rotation.x += deltaMove.y * rotationSpeed;

      setRotationVelocity({
        x: deltaMove.y * rotationSpeed,
        y: deltaMove.x * rotationSpeed,
      });

      setPreviousMousePosition({ x: event.clientX, y: event.clientY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      resetInactivityTimer();
    };

    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    setShouldAutoRotate(true);

    return () => {
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [isDragging, previousMousePosition]);

  useFrame(({ clock }) => {
    if (!globeRef.current) return;

    if (!isDragging) {
      globeRef.current.rotation.x += rotationVelocity.x;
      globeRef.current.rotation.y += rotationVelocity.y;

      setRotationVelocity((prev) => ({
        x: prev.x * 0.95,
        y: prev.y * 0.95,
      }));

      if (shouldAutoRotate && isRotationStopped()) {
        globeRef.current.rotation.y += autoRotationSpeed;
      }
    }
  });

  return (
    <>
      <color attach="background" args={[backgroundColor]} />

      <ambientLight intensity={1} />
      <pointLight position={[10, 10, 10]} intensity={2} />
      <pointLight position={[-10, -10, -10]} intensity={1} />
      <directionalLight position={[5, 5, 5]} intensity={2} />
      <Environment files="/hdri/studio_small_03_1k.hdr" />

      <mesh ref={globeRef}>
        <sphereGeometry args={[GLOBE_RADIUS, 64, 64]} />
        <meshStandardMaterial map={globeMap} metalness={0.4} roughness={0.7} />

        {cityPositions.map((position, i) => (
          <CityDot key={i} position={position} />
        ))}
      </mesh>

      <Ring rotation={[Math.PI / 4, 0, 0]} speed={0.8} scale={1.1} />
      <Ring rotation={[-Math.PI / 6, Math.PI / 3, 0]} speed={1.2} scale={1.15} />
    </>
  );
}
